import { Fragment, useEffect, useMemo } from 'react';
import type { JSX } from 'react';
import { useI18n } from '../i18n';
import type { CheckLoop } from '../hooks/useCheckLoop';
import { averageResponseMs } from '../lib/averageResponseMs';
import type { NetStatus } from '../lib/networkStatus';
import type { Phase } from '../lib/runner';
import { LastCheckBanner } from './LastCheckBanner';
import { CompactResultRow } from './ui/CompactResultRow';
import { Icon } from './ui/Icon';
import {
  Module,
  ModuleDivider,
  ModuleLabel,
  StatCell,
  StatCellDivider,
  StatRow,
} from './ui/Module';
import { NeonButton, NeonOutlinedButton } from './ui/NeonButton';
import { ConnectionModule, NoInternetBanner } from './ui/NetworkStatusUi';
import { NoteRow } from './ui/NoteRow';
import { LinearProgress } from './ui/ProgressBar';
import { ScreenContent, TopAppBar } from './ui/ScreenScaffold';

/**
 * The dashboard — a port of `android/.../ui/home/HomeScreen.kt`, block for block
 * and in the same order.
 *
 * What Android splits between a `HomeViewModel.UiState` and a `HomeRoute` is
 * split here between `useCheckLoop` and this file: the hook owns the scheduler
 * and the cycle, this file owns nothing but the reading of it. Every number on
 * screen is derived from `loop`; none of it is decided here. That is the same
 * rule `src/lib` follows and for the same reason — the moment a screen starts
 * computing what a run measured, the thing that decides what reaches the backend
 * stops being testable without a DOM.
 *
 * ### What Android has here that this cannot
 *
 * Two of Android's `ProgressNotice` branches are **omitted rather than
 * substituted**, and both omissions are permanent:
 *
 * - `Phase.VERIFYING_NETWORK` / `home_verifying_network`. Android runs a real
 *   reachability probe against a known endpoint before it trusts the
 *   connectivity manager, and on a dead network that probe takes long enough to
 *   need a line saying the app has not hung. Here the equivalent question is
 *   answered by `navigator.onLine`, synchronously and with no visible wait —
 *   there is no state to narrate, which is why the string was not ported either.
 * - `home_browser_round`. Android re-loads suspicious dead links in a real
 *   WebView to catch bot walls, which costs seconds per link instead of
 *   milliseconds and therefore needs its own notice. **There is no browser
 *   verification round inside Safari and no way to build one** (SPEC §14 #8,
 *   `ios-webclip/CLAUDE.md`), so the branch has nothing to report and the
 *   `Public` icon Android draws for it is not in the icon set.
 *
 * Android's offline snackbar (`err_offline_cannot_start`) is absent for a
 * related reason: it fires when the operator taps Start with no network, and a
 * cycle here does not refuse — it parks in `waiting-for-network` and resumes on
 * its own. The notice row under the bar says that permanently rather than for
 * four seconds.
 */

/** Android's `RECENT_LIMIT`. Four rows is a glance; a fifth is a list. */
const RECENT_LIMIT = 4;

export function HomeScreen({
  loop,
  status,
  active,
  onViewResults,
  onOpenLanguage,
  onKeyRevoked,
}: {
  loop: CheckLoop;
  status: NetStatus;
  /**
   * Whether this pane is the tab on screen (`route === 'home'` in the shell).
   *
   * The shell keeps all three panes mounted so a trip to Settings does not throw
   * away the Home scroll position or the Results filter — which means a hidden
   * Home is still running its effects. The only one that costs anything is the
   * banner's once-a-second clock, so `active` is passed down to suspend it
   * rather than having every tab pay for a countdown nobody is looking at.
   */
  active: boolean;
  onViewResults(): void;
  onOpenLanguage(): void;
  /** Called once when the backend rejects the key. See the effect below. */
  onKeyRevoked(): void;
}): JSX.Element {
  const { t, n } = useI18n();

  const average = useMemo(() => averageResponseMs(loop.shown), [loop.shown]);
  const hasResults = loop.shown.length > 0;

  /**
   * `401` is terminal (`ios-webclip/CLAUDE.md`): the key lives in the WebClip
   * icon's URL, so there is nothing this app can re-authenticate with and
   * nothing a retry could achieve.
   *
   * Where that gets *said* is the part worth being careful about. This screen
   * hands the event up and lets the shell return to the key-entry gate, which is
   * exactly what Android's `HomeRoute` does with
   * `LaunchedEffect(state.needsKey) { onNeedKey() }` — the gate owns
   * `ios_key_revoked_title` / `_body` / `_hint`, shows the masked key so an
   * operator can tell an administrator *which* device to re-provision, and is
   * somewhere they can actually act. A terminal panel *here* was a dead end with
   * no way out of it and has already been removed once; it is not coming back,
   * and there is deliberately no retry button anywhere — this app cannot change
   * the key in its own icon's URL, so a retry would re-send the same rejected
   * key and collect the same `401` forever.
   */
  useEffect(() => {
    if (loop.revoked) onKeyRevoked();
  }, [loop.revoked, onKeyRevoked]);

  const languageAction = (
    <button
      type="button"
      className="icon-btn icon-btn--accent"
      onClick={onOpenLanguage}
      aria-label={t('a11y_change_language')}
    >
      {/* 24px — Material's IconButton default, and Android's `IconSize.prominent`. */}
      <Icon name="language" size={24} />
    </button>
  );

  const totalRounds = loop.totalRounds > 0 ? loop.totalRounds : loop.config.checkRounds;
  const fraction = loop.total > 0 ? loop.checked / loop.total : 0;
  // `.toInt()` on Android, so truncation rather than rounding: a bar at 99.6%
  // must not print 100% while four links are still being probed.
  const percent = Math.floor(fraction * 100);

  return (
    <>
      <TopAppBar title={t('app_name')} actions={languageAction} />

      {/*
       * The connection block scrolls with everything else, as it does on
       * Android. It used to be pinned under the app bar, which cost a permanent
       * slice of a screen whose actual job is the progress number.
       */}
      <ScreenContent>
        <NoInternetBanner status={status} />

        {/*
         * This app's own module, ahead of everything Android shows, because on a
         * foreground-only device "when did this last report" outranks "what is it
         * doing right now" — see the note in LastCheckBanner.tsx.
         */}
        <LastCheckBanner
          lastSuccessAt={loop.lastSuccessAt}
          intervalMinutes={loop.config.checkIntervalMinutes}
          nextRunAt={loop.nextRunAt}
          running={loop.running}
          active={active}
        />

        <ConnectionModule
          status={status}
          ipInfo={loop.context?.ipInfo ?? null}
          carrier={loop.context?.carrier}
          region={loop.context?.region}
        />

        <Module>
          <div className="module__body">
            <ModuleLabel text={t('home_progress_label')} />
            <p className="progress-module__percent n-hero clamp-1">
              {t('home_percent', n(percent))}
            </p>
            {/*
             * The 250ms tween Android applies with `animateFloatAsState` is a
             * CSS transition on `.progress-track__fill`, so it costs no state
             * update per frame and is already inside the reduced-motion guard.
             */}
            <LinearProgress
              value={fraction}
              label={t('home_progress_label')}
              className="progress-module__bar"
            />
            <p className="progress-module__counts n-small clamp-1">
              {t('home_checked_of_total', n(loop.checked), n(loop.total))}
            </p>

            {/*
             * The round line: a bar that restarts three times needs to say why,
             * and `dead` shrinking as later rounds rescue links is the one thing
             * an operator actually sees the multi-round design do.
             *
             * Android gates this on `phase != IDLE` because its ViewModel seeds
             * `round` at 1. Here a round number only exists once the runner has
             * reported one, so the gate is `round > 0` — same visible outcome,
             * and it also keeps the line off the screen during the config fetch,
             * where "Round 0/3" would be true of nothing.
             */}
            {totalRounds > 1 && loop.round > 0 ? (
              <p className="progress-module__round t-body-small clamp-1">
                {t('home_round', n(loop.round), n(totalRounds), n(loop.dead))}
              </p>
            ) : null}

            <ProgressNotice phase={loop.phase} />
          </div>
        </Module>

        <Module>
          <StatRow>
            <StatCell
              value={n(loop.alive)}
              // No question mark on a stat-cell label, unlike a result row: the
              // cell is a count, and the caveat about what "alive" can mean on
              // this platform belongs to the rows that make the claim per link.
              label={t('stat_alive')}
              // A `.c-*` utility, never `valueColor="var(--alive)"`. An inline
              // style object reaches the DOM invisibly to a `style={{` grep and
              // puts the theme back in the components, which is the one thing
              // this port exists to undo (`components.css`, opening note).
              className="c-alive"
            />
            <StatCellDivider />
            <StatCell value={n(loop.dead)} label={t('stat_dead')} className="c-dead" />
            <StatCellDivider />
            <StatCell
              // Em dash, not "0": no successful check means there is nothing to
              // report, which is a different claim from "it answered instantly".
              value={average === null ? t('value_none') : t('unit_ms', n(average))}
              label={t('stat_response')}
            />
          </StatRow>
        </Module>

        <BatchSummary
          saved={loop.lastSaved}
          skipped={loop.lastSkipped}
          dropped={loop.droppedCount}
        />

        {hasResults ? (
          <Module>
            <div className="recent-module__header">
              <ModuleLabel text={t('home_recent_label')} className="recent-module__label" />
              <button type="button" className="text-btn" onClick={onViewResults}>
                <span className="t-label-large clamp-1">{t('common_see_all')}</span>
              </button>
            </div>
            {loop.shown.slice(0, RECENT_LIMIT).map((result) => (
              <Fragment key={result.link.id}>
                <ModuleDivider />
                <CompactResultRow result={result} />
              </Fragment>
            ))}
          </Module>
        ) : null}

        {/*
         * `revoked` also lands the phase on `failed`, and it is handled by the
         * effect above rather than by an error module — a key that will never be
         * accepted again is not "something went wrong, try again later".
         */}
        {loop.phase.kind === 'failed' && !loop.revoked ? (
          <ErrorModule reason={loop.lastError} />
        ) : null}

        {loop.running ? (
          <NeonOutlinedButton
            text={t('home_stop')}
            onClick={loop.cancel}
            icon="stop"
            className="home-primary"
          />
        ) : (
          <NeonButton
            text={hasResults ? t('home_run_again') : t('home_start')}
            onClick={loop.runNow}
            icon="play-arrow"
            className="home-primary"
          />
        )}

        {hasResults ? (
          <NeonOutlinedButton text={t('home_view_results')} onClick={onViewResults} />
        ) : null}
      </ScreenContent>
    </>
  );
}

/**
 * The one-line explanation under the progress bar for a run that is not
 * advancing — Android's `private fun ProgressNotice`, one branch per phase that
 * looks like a hang from the outside.
 *
 * See the file note for the two branches Android has and this cannot.
 */
function ProgressNotice({ phase }: { phase: Phase }): JSX.Element | null {
  const { t, n, seconds } = useI18n();
  switch (phase.kind) {
    case 'waiting-for-network':
      return (
        <NoteRow className="progress-notice" icon="cloud-off" text={t('home_waiting_network')} />
      );
    case 'fetching':
      return (
        <NoteRow className="progress-notice" icon="cloud-off" text={t('ios_phase_fetching')} />
      );
    case 'between-rounds':
      return (
        <NoteRow
          className="progress-notice"
          icon="schedule"
          text={t('home_next_round', n(phase.nextRound), seconds(phase.secondsLeft))}
          // Not an error: the gap between rounds is the point of the whole
          // design. Legitimately `--primary` because it tints a *graphic* —
          // 3.84:1 clears the 3:1 WCAG asks of a non-text element, and the
          // sentence beside it stays --text-secondary.
          tint="c-primary-graphic"
        />
      );
    case 'submitting':
      return (
        <NoteRow
          className="progress-notice"
          icon="schedule"
          text={t('ios_phase_submitting')}
          tint="c-primary-graphic"
        />
      );
    // idle / checking / done / failed are told by the bar, the counts and the
    // error module; a notice row for each would narrate the obvious.
    default:
      return null;
  }
}

/**
 * What the backend did with the last batch, under the stats.
 *
 * `ios_batch_dropped` is why this block exists and is **not optional**. A link
 * that got fewer rounds than the config asked for has not been proven dead, so
 * `aggregate` drops it and it never reaches the backend — and a rule that
 * silently discards data while being impossible to measure is one nobody ever
 * notices going wrong (`ios-webclip/CLAUDE.md`, hard invariants). This count is
 * the only place it is measurable, so it renders on its own condition rather
 * than inside the `saved` line, and it is tinted `--warn` rather than left as
 * quiet secondary text: the line above it is a receipt, this one is a hole in
 * the data.
 *
 * Android has no counterpart because it has no equivalent gap to report — its
 * runs are not cut short by the screen going off mid-cycle.
 */
function BatchSummary({
  saved,
  skipped,
  dropped,
}: {
  saved: number | null;
  skipped: number | null;
  dropped: number;
}): JSX.Element | null {
  const { t, n } = useI18n();
  if (saved === null && dropped === 0) return null;
  return (
    <div className="home-batch">
      {saved === null ? null : (
        <p className="t-body-small c-text-secondary">
          {/*
           * One key per sentence, not a saved line with a comma-clause glued on
           * after it. Concatenating two translated fragments hands the word
           * order to whichever language the code was written in, and the
           * languages this app ships do not agree on it — `ios_batch_saved` and
           * `ios_batch_saved_skipped` let each locale write the whole sentence.
           * Skipped is hidden at zero either way: "0 skipped" is noise on every
           * healthy cycle.
           */}
          {skipped !== null && skipped > 0
            ? t('ios_batch_saved_skipped', n(saved), n(skipped))
            : t('ios_batch_saved', n(saved))}
        </p>
      )}
      {dropped > 0 ? <p className="t-body-small c-warn">{t('ios_batch_dropped', n(dropped))}</p> : null}
    </div>
  );
}

/** Port of Android's `private fun ErrorModule`. */
function ErrorModule({ reason }: { reason: string | null }): JSX.Element {
  const { t } = useI18n();
  return (
    <Module>
      <div className="module__row module__row--top">
        <Icon name="error-outline" className="c-error" />
        <div className="home-error__text">
          <p className="t-title-small c-text-primary">{t('err_generic_title')}</p>
          {/*
           * The runner's own reason, verbatim, and deliberately not translated.
           * These strings name an internal condition ("offline before submit",
           * "no links to check", "nothing measured") and exist to be read back to
           * whoever maintains the backend; inventing a translated key per failure
           * mode would either lose that detail or promise a friendlier diagnosis
           * than the app can support. `err_try_again_later` covers the case where
           * there is no reason at all.
           */}
          <p className="t-body-medium c-text-secondary break-anywhere">
            {reason ?? t('err_try_again_later')}
          </p>
        </div>
      </div>
    </Module>
  );
}
